import React, {FC, useState, useMemo, useEffect, useCallback} from 'react';
import {
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';

import {ReactComponent as ArrowRightOutlinedIcon} from '../../../assests/svg/right-icon-large.svg';
import {ReactComponent as ArrowRightOutlinedIconActive} from '../../../assests/svg/right-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleRight} from '../../../assests/svg/double-right-icon.svg';
import {ReactComponent as ArrowDoubbleRightInactive} from '../../../assests/svg/double-icon-right-inactive.svg';
import {ReactComponent as ArrowLeftOutlinedIcon} from '../../../assests/svg/left-icon-large.svg';
import {ReactComponent as ArrowLeftOutlinedIconActive} from '../../../assests/svg/left-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleLeft} from '../../../assests/svg/double-left-icon.svg';
import {ReactComponent as ArrowDoubbleLeftActive} from '../../../assests/svg/double-left-active.svg';
import {ReactComponent as ChackedIcon} from '../../../assests/svg/checkmark-blue.svg';
import AurSearchBox from '../aur-search-box/AurSearchBox';
import {isEmpty} from '../../../utils/utils';
import {t} from 'i18next';
import {difference, differenceBy, intersection, union} from 'lodash';

export type KeyValue = {
  key: string;
  label: string;
};

type AurDuelTransferListProps = {
  availableFieldsOrignal: KeyValue[];
  selectedFieldsOrignal?: KeyValue[];
  isDisabled?: boolean;
  onSelectedFieldsChange?: (fields: KeyValue[]) => void;
};

type AurDuelTransferListType = 'available' | 'selected';

const AurDuelTransferList: FC<AurDuelTransferListProps> = ({
  availableFieldsOrignal,
  selectedFieldsOrignal = [],
  isDisabled = false,
  onSelectedFieldsChange,
}) => {
  const [selectedFields, setSelectedFields] = useState<KeyValue[]>(
    selectedFieldsOrignal
  );
  const [selectedAvailable, setSelectedAvailable] = useState<KeyValue[]>([]);
  const [selectedSelected, setSelectedSelected] = useState<KeyValue[]>([]);

  const [searchTermAvailable, setSearchTermAvailable] = useState('');
  const [searchTermSelected, setSearchTermSelected] = useState('');

  const availableFields = useMemo(() => {
    const selectedKeys = new Set(selectedFields.map((s) => s.key));
    return availableFieldsOrignal.filter(
      (field) => !selectedKeys.has(field.key)
    );
  }, [availableFieldsOrignal, selectedFields]);

  const displayedAvailableFields = useMemo(() => {
    return availableFields
      .filter((field) =>
        !isEmpty(searchTermAvailable?.trim())
          ? field.label
              .toLowerCase()
              .includes(searchTermAvailable.toLowerCase())
          : true
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableFields, searchTermAvailable]);

  const displayedSelectedFields = useMemo(() => {
    return selectedFields
      .filter((field) =>
        !isEmpty(searchTermSelected?.trim())
          ? field.label.toLowerCase().includes(searchTermSelected.toLowerCase())
          : true
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [selectedFields, searchTermSelected]);

  const commonAvailableFields = useMemo(() => {
    return intersection(availableFields, displayedAvailableFields);
  }, [availableFields, displayedAvailableFields]);

  const commonSelectedFields = useMemo(() => {
    return intersection(selectedFields, displayedSelectedFields);
  }, [displayedSelectedFields, selectedFields]);

  const commonSelectedAvailableFields = useMemo(() => {
    return intersection(selectedAvailable, displayedAvailableFields);
  }, [displayedAvailableFields, selectedAvailable]);

  const commonSelectedSelectedFields = useMemo(() => {
    return intersection(selectedSelected, displayedSelectedFields);
  }, [displayedSelectedFields, selectedSelected]);

  const handleSearchChange = useCallback(
    (search: string, type: AurDuelTransferListType) => {
      if (type === 'available') {
        setSelectedAvailable([]);
        setSearchTermAvailable(search);
      } else {
        setSelectedSelected([]);
        setSearchTermSelected(search);
      }
    },
    []
  );

  const handleToggle = useCallback(
    (
      field: KeyValue,
      selectedList: KeyValue[],
      setSelectedList: React.Dispatch<React.SetStateAction<KeyValue[]>>
    ) => {
      setSelectedList((prev) =>
        prev.some((item) => item.key === field.key)
          ? prev.filter((item) => item.key !== field.key)
          : [...prev, field]
      );
    },
    []
  );

  const moveToAllSelected = useCallback(() => {
    const updatedSelectedFields = union(selectedFields, commonAvailableFields);
    setSelectedAvailable([]);
    setSelectedSelected([]);
    setSelectedFields(updatedSelectedFields);
    onSelectedFieldsChange?.(updatedSelectedFields); // notify parent
  }, [commonAvailableFields, onSelectedFieldsChange, selectedFields]);

  const moveToAllAvailable = useCallback(() => {
    const updatedSelectedFields = difference(
      selectedFields,
      commonSelectedFields
    );
    setSelectedAvailable([]);
    setSelectedSelected([]);
    setSelectedFields(updatedSelectedFields);
    onSelectedFieldsChange?.(updatedSelectedFields); // notify parent
  }, [onSelectedFieldsChange, selectedFields, commonSelectedFields]);

  const moveToSelected = useCallback(() => {
    const updated = selectedAvailable.filter(
      (f) => !selectedFields.some((s) => s.key === f.key)
    );
    const newSelectedFields = [...selectedFields, ...updated];
    setSelectedFields(newSelectedFields);
    setSelectedAvailable([]);
    onSelectedFieldsChange?.(newSelectedFields); // notify parent
  }, [onSelectedFieldsChange, selectedAvailable, selectedFields]);

  const moveToAvailable = useCallback(() => {
    const newSelectedFields = selectedFields.filter(
      (f) => !selectedSelected.some((s) => s.key === f.key)
    );
    setSelectedFields(newSelectedFields);
    setSelectedSelected([]);
    onSelectedFieldsChange?.(newSelectedFields); // notify parent
  }, [onSelectedFieldsChange, selectedFields, selectedSelected]);

  const isFieldSelected = useCallback(
    (field: KeyValue, selectedList: KeyValue[]) =>
      selectedList.some((item) => item.key === field.key),
    []
  );

  useEffect(() => {
    // Optional: notify parent on mount if selectedFieldsOrignal provided
    if (selectedFieldsOrignal?.length) {
      onSelectedFieldsChange?.(selectedFieldsOrignal);
    }
  }, [onSelectedFieldsChange, selectedFieldsOrignal]); // run once on mount

  useEffect(() => {
    if (!isEmpty(differenceBy(selectedFields, selectedFieldsOrignal, 'key'))) {
      setSelectedFields(selectedFieldsOrignal);
    }
  }, [selectedFields, selectedFieldsOrignal]);

  useEffect(() => {
    if (isDisabled) {
      setSelectedFields([]);
      setSelectedAvailable([]);
      setSelectedSelected([]);
      setSearchTermAvailable('');
      setSearchTermSelected('');
      onSelectedFieldsChange?.([]);
    }
  }, [isDisabled, onSelectedFieldsChange]);

  const isDisplayedListEmptyByType = useCallback(
    (type: AurDuelTransferListType) =>
      type === 'available'
        ? !isEmpty(searchTermAvailable?.trim()) &&
          isEmpty(displayedAvailableFields)
        : !isEmpty(searchTermSelected?.trim()) &&
          isEmpty(displayedSelectedFields),
    [
      displayedAvailableFields,
      displayedSelectedFields,
      searchTermAvailable,
      searchTermSelected,
    ]
  );

  const renderNoDataAvailable = useCallback(
    (type: AurDuelTransferListType) => {
      return isDisplayedListEmptyByType(type) ? (
        <Box className={'no-data-available-container'}>
          <Typography className="typo-text body3_size field-label-text">
            {t('common.noDataAvailable')}
          </Typography>
        </Box>
      ) : null;
    },
    [isDisplayedListEmptyByType]
  );

  return (
    <Box className="aur-duel-transfer-list-container">
      <Box className="available-fields">
        {isDisabled ? (
          <Box className={'transfer-list-disable-container'} />
        ) : null}
        <Box className="available-fields-search-header">
          <Typography className="typo-text subhead4_size text box-sub-heading">
            {t('queryBuilder.available')}
          </Typography>

          <AurSearchBox
            dataTestId={'available-search-textfield'}
            value={searchTermAvailable}
            autoComplete={'off'}
            classNameProps="searchbox-field"
            variant={'outlined'}
            label={''}
            placeHolder={t('common.search')}
            onSearch={(e) => handleSearchChange(e, 'available')}
          />
          <Divider />
        </Box>
        <Box className="names-list">
          {renderNoDataAvailable('available')}
          <List>
            {displayedAvailableFields.map((field) => (
              <ListItem
                key={field.key}
                button
                onClick={() =>
                  handleToggle(field, selectedAvailable, setSelectedAvailable)
                }
                selected={isFieldSelected(field, selectedAvailable)}
                className="field-text"
                data-testid={`available-list-item-${field.key}`}
              >
                <Typography className="body3_size">
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        className="typo-text body3_size field-label-text"
                        noWrap
                      >
                        {field.label}
                      </Typography>
                    }
                  />
                </Typography>
                {isFieldSelected(field, selectedAvailable) && <ChackedIcon />}
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>

      <Box className="arrow-control">
        {isDisabled ? (
          <Box className={'arrow-control-disable-container'} />
        ) : null}
        <div className="left-to-right">
          <IconButton
            disabled={isEmpty(displayedAvailableFields)}
            onClick={moveToAllSelected}
            className="shev-right"
            data-testid="move-all-available-button"
          >
            {isEmpty(displayedAvailableFields) ? (
              <ArrowDoubbleRightInactive />
            ) : (
              <ArrowDoubbleRight />
            )}
          </IconButton>
          <IconButton
            disabled={isEmpty(commonSelectedAvailableFields)}
            onClick={moveToSelected}
            className="shev-right"
            data-testid="move-available-button"
          >
            {!isEmpty(commonSelectedAvailableFields) ? (
              <ArrowRightOutlinedIconActive />
            ) : (
              <ArrowRightOutlinedIcon />
            )}
          </IconButton>
        </div>
        <Divider className="control-divider" />
        <div className="left-to-right">
          <IconButton
            disabled={isEmpty(commonSelectedSelectedFields)}
            onClick={moveToAvailable}
            className="shev-right"
            data-testid="move-selected-button"
          >
            {!isEmpty(commonSelectedSelectedFields) ? (
              <ArrowLeftOutlinedIconActive />
            ) : (
              <ArrowLeftOutlinedIcon />
            )}
          </IconButton>
          <IconButton
            disabled={isEmpty(displayedSelectedFields)}
            onClick={moveToAllAvailable}
            className="shev-right"
            data-testid="move-all-selected-button"
          >
            {isEmpty(displayedSelectedFields) ? (
              <ArrowDoubbleLeft />
            ) : (
              <ArrowDoubbleLeftActive />
            )}
          </IconButton>
        </div>
      </Box>

      <Box className="available-fields">
        {isDisabled ? (
          <Box className={'transfer-list-disable-container-selected'} />
        ) : null}
        <Box className="available-fields-search-header">
          <Typography className="typo-text subhead4_size text box-sub-heading ">
            {t('queryBuilder.selected')}
          </Typography>
          <AurSearchBox
            dataTestId={'selected-search-textfield'}
            value={searchTermSelected}
            autoComplete={'off'}
            classNameProps="searchbox-field"
            variant={'outlined'}
            label={''}
            placeHolder={t('common.search')}
            onSearch={(e) => handleSearchChange(e, 'selected')}
          />
          <Divider />
        </Box>
        <Box className="names-list">
          {renderNoDataAvailable('selected')}
          <List>
            {displayedSelectedFields.map((field) => (
              <ListItem
                key={field.key}
                button
                onClick={() =>
                  handleToggle(field, selectedSelected, setSelectedSelected)
                }
                selected={isFieldSelected(field, selectedSelected)}
                className="field-text"
                data-testid={`selected-list-item-${field.key}`}
              >
                <Typography className="body3_size">
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        className="typo-text body3_size field-label-text"
                        noWrap
                      >
                        {field.label}
                      </Typography>
                    }
                  />
                </Typography>
                {isFieldSelected(field, selectedSelected) && <ChackedIcon />}
              </ListItem>
            ))}
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default AurDuelTransferList;
